/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */
import { __ } from '@oat-sa-private/ui-core';
import { uploadServiceStatuses } from '../../../../services/upload/attachmentsService.js';
import { createRecordingEvidence } from './helpers.js';
import {
    createSandboxVideoRecordingResponse,
    sandboxLocalResponsePersistenceStrategy
} from './sandboxUploadAdapter.js';

export const genericAttachmentPersistenceStrategy = 'generic-attachment-fileHash';

export async function persistRecordedVideoFile({
    file,
    recorderMimeType,
    recordingDurationMs,
    localPreviewUrl,
    itemIdentifier,
    responseIdentifier,
    attachmentsService,
    pendingOperationsStore,
    recordKey,
    uploadKey,
    uploadStats,
    logger,
    setPersistenceState,
    setUploadError,
    setResponseNotice,
    setEvidence,
    setAnnouncement,
    persistState,
    ariaLiveStrings
}) {
    pendingOperationsStore.add(uploadKey);
    setUploadError('');
    setResponseNotice('');
    uploadStats.bytesLoaded = 0;
    uploadStats.bytesTotal = 0;

    function handleLocalPlaybackFallback(error) {
        setUploadError(
            __(
                'Unable to upload this recording. You can still play it back locally in this session, but it will stay unanswered until the upload succeeds.'
            )
        );
        setResponseNotice(__('Local playback is still available in this session. Retry the recording or upload before you submit.'));
        logger?.error(error);
        persistState(null, false, {
            persistLocalPreview: true,
            localPreviewUrlOverride: localPreviewUrl,
            localSessionFile: file
        });
        setAnnouncement(ariaLiveStrings.cancelled);
    }

    try {
        setAnnouncement(ariaLiveStrings.started);
        if (!attachmentsService.uploadService) {
            setPersistenceState(uploadServiceStatuses.loading);
            await attachmentsService.initializeUploadService();
        }

        if (attachmentsService.uploadService?.getBaseType() !== 'fileHash') {
            const sandboxResponse = await createSandboxVideoRecordingResponse({
                file,
                itemIdentifier,
                responseIdentifier,
                recordingDurationMs,
                recorderMimeType,
                localPreviewUrl
            });

            setResponseNotice(__(sandboxResponse.message));
            setEvidence(
                createRecordingEvidence({
                    sizeBytes: file.size,
                    durationMs: recordingDurationMs,
                    mimeType: recorderMimeType,
                    persistenceStrategy:
                        sandboxResponse.responseValue.persistenceStrategy || sandboxLocalResponsePersistenceStrategy
                })
            );
            persistState(sandboxResponse.responseValue, true, {
                localSessionFile: file
            });
            setAnnouncement(ariaLiveStrings.completed);
            return;
        }

        setPersistenceState(uploadServiceStatuses.uploading);
        const uploadStartedAt = Date.now();
        const uploadResult = await attachmentsService.uploadService.upload(
            { data: file },
            {
                onProgress(bytesLoaded, bytesTotal) {
                    uploadStats.bytesLoaded = bytesLoaded;
                    uploadStats.bytesTotal = bytesTotal;
                }
            }
        );

        if (!uploadResult) {
            handleLocalPlaybackFallback(new Error('Video upload returned no response value.'));
            return;
        }

        const responseValue = {
            ...uploadResult,
            mime: recorderMimeType,
            sizeBytes: file.size,
            durationMs: recordingDurationMs,
            recorderMimeType,
            uploadDurationMs: Date.now() - uploadStartedAt,
            persistenceStrategy: genericAttachmentPersistenceStrategy
        };

        setEvidence(
            createRecordingEvidence({
                sizeBytes: file.size,
                durationMs: recordingDurationMs,
                mimeType: recorderMimeType,
                uploadDurationMs: responseValue.uploadDurationMs,
                persistenceStrategy: responseValue.persistenceStrategy
            })
        );
        persistState(responseValue);
        setAnnouncement(ariaLiveStrings.completed);
    } catch (error) {
        handleLocalPlaybackFallback(error);
    } finally {
        setPersistenceState(uploadServiceStatuses.ready);
        pendingOperationsStore.delete(uploadKey);
        pendingOperationsStore.delete(recordKey);
        uploadStats.bytesLoaded = 0;
        uploadStats.bytesTotal = 0;
    }
}
